import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  TextField,
  Select,
  FormControl,
  InputLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import loansService, { Loan, LoanFilters } from '../../services/loansService';

interface LoanListProps {
  onCreateLoan?: () => void;
  onEditLoan?: (loan: Loan) => void;
  onViewLoan?: (loan: Loan) => void;
  clientId?: string; // If provided, show only loans for this client
}

const LoanList: React.FC<LoanListProps> = ({
  onCreateLoan,
  onEditLoan,
  onViewLoan,
  clientId
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState<string>('createdAt');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedLoan, setSelectedLoan] = useState<Loan | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [filters, setFilters] = useState<LoanFilters>({
    clientId: clientId || undefined,
    page: 1,
    limit: 10,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  const queryClient = useQueryClient();

  const { data: loansData, isLoading, error } = useQuery({
    queryKey: ['loans', filters],
    queryFn: () => loansService.listLoans(filters)
  });

  const deleteLoanMutation = useMutation({
    mutationFn: (id: string) => loansService.deleteLoan(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['loans'] });
      setDeleteDialogOpen(false);
      setSelectedLoan(null);
    }
  });

  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      page: page + 1,
      limit: rowsPerPage,
      sortBy: orderBy,
      sortOrder: order
    }));
  }, [page, rowsPerPage, orderBy, order]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, loan: Loan) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedLoan(loan);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedLoan(null);
  };

  const handleDelete = () => {
    if (selectedLoan) {
      setDeleteDialogOpen(true);
    }
    handleMenuClose();
  };

  const confirmDelete = () => {
    if (selectedLoan) {
      deleteLoanMutation.mutate(selectedLoan.id);
    }
  };

  const handleFilterChange = (key: keyof LoanFilters, value: string | number | undefined) => {
    setFilters(prev => ({
      ...prev,
      [key]: value === '' ? undefined : value
    }));
    setPage(0);
  };

  const canDeleteLoan = (loan: Loan): boolean => {
    return ['Application', 'Denied', 'Charged Off'].includes(loan.status);
  };

  if (error) {
    return (
      <Alert severity="error">
        Failed to load loans. Please try again.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          {clientId ? 'Client Loans' : 'Loan Management'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<FilterIcon />}
            onClick={() => setShowFilters(!showFilters)}
            variant={showFilters ? 'contained' : 'outlined'}
          >
            Filters
          </Button>
          <Button
            startIcon={<DownloadIcon />}
            variant="outlined"
          >
            Export
          </Button>
          {onCreateLoan && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onCreateLoan}
            >
              New Loan
            </Button>
          )}
        </Box>
      </Box>

      {/* Filters */}
      {showFilters && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Filters</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status || ''}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="Application">Application</MenuItem>
                    <MenuItem value="Under Review">Under Review</MenuItem>
                    <MenuItem value="Additional Info Required">Additional Info Required</MenuItem>
                    <MenuItem value="Approved">Approved</MenuItem>
                    <MenuItem value="Funded">Funded</MenuItem>
                    <MenuItem value="Current">Current</MenuItem>
                    <MenuItem value="Past Due">Past Due</MenuItem>
                    <MenuItem value="Default">Default</MenuItem>
                    <MenuItem value="Paid Off">Paid Off</MenuItem>
                    <MenuItem value="Charged Off">Charged Off</MenuItem>
                    <MenuItem value="Denied">Denied</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>Loan Type</InputLabel>
                  <Select
                    value={filters.loanType || ''}
                    onChange={(e) => handleFilterChange('loanType', e.target.value)}
                    label="Loan Type"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="Term Loan">Term Loan</MenuItem>
                    <MenuItem value="Line of Credit">Line of Credit</MenuItem>
                    <MenuItem value="Equipment Financing">Equipment Financing</MenuItem>
                    <MenuItem value="SBA Loan">SBA Loan</MenuItem>
                    <MenuItem value="Merchant Cash Advance">Merchant Cash Advance</MenuItem>
                    <MenuItem value="Invoice Factoring">Invoice Factoring</MenuItem>
                    <MenuItem value="Real Estate Loan">Real Estate Loan</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  fullWidth
                  label="Min Amount"
                  type="number"
                  value={filters.amountMin || ''}
                  onChange={(e) => handleFilterChange('amountMin', e.target.value ? parseInt(e.target.value) : undefined)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  fullWidth
                  label="Max Amount"
                  type="number"
                  value={filters.amountMax || ''}
                  onChange={(e) => handleFilterChange('amountMax', e.target.value ? parseInt(e.target.value) : undefined)}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Loans Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'loanNumber'}
                    direction={orderBy === 'loanNumber' ? order : 'asc'}
                    onClick={() => handleRequestSort('loanNumber')}
                  >
                    Loan #
                  </TableSortLabel>
                </TableCell>
                {!clientId && (
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'client.businessName'}
                      direction={orderBy === 'client.businessName' ? order : 'asc'}
                      onClick={() => handleRequestSort('client.businessName')}
                    >
                      Client
                    </TableSortLabel>
                  </TableCell>
                )}
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'type'}
                    direction={orderBy === 'type' ? order : 'asc'}
                    onClick={() => handleRequestSort('type')}
                  >
                    Type
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderBy === 'requestedAmount'}
                    direction={orderBy === 'requestedAmount' ? order : 'asc'}
                    onClick={() => handleRequestSort('requestedAmount')}
                  >
                    Amount
                  </TableSortLabel>
                </TableCell>
                <TableCell>Status</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'applicationDate'}
                    direction={orderBy === 'applicationDate' ? order : 'asc'}
                    onClick={() => handleRequestSort('applicationDate')}
                  >
                    Applied
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={clientId ? 6 : 7} align="center">
                    Loading loans...
                  </TableCell>
                </TableRow>
              ) : loansData?.loans.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={clientId ? 6 : 7} align="center">
                    No loans found
                  </TableCell>
                </TableRow>
              ) : (
                loansData?.loans.map((loan) => (
                  <TableRow key={loan.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {loan.loanNumber}
                      </Typography>
                    </TableCell>
                    {!clientId && (
                      <TableCell>
                        <Typography variant="body2">
                          {loan.client?.businessName || 'Unknown Client'}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography variant="body2">
                        {loan.type}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium">
                        {loansService.formatCurrency(loan.requestedAmount)}
                      </Typography>
                      {loan.approvedAmount && loan.approvedAmount !== loan.requestedAmount && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Approved: {loansService.formatCurrency(loan.approvedAmount)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={loan.status}
                        color={loansService.getStatusColor(loan.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(loan.applicationDate).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, loan)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {loansData && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={loansData.pagination.total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Paper>

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => { onViewLoan?.(selectedLoan!); handleMenuClose(); }}>
          <ViewIcon sx={{ mr: 1 }} fontSize="small" />
          View Details
        </MenuItem>
        <MenuItem onClick={() => { onEditLoan?.(selectedLoan!); handleMenuClose(); }}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Edit
        </MenuItem>
        {selectedLoan && canDeleteLoan(selectedLoan) && (
          <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
            Delete
          </MenuItem>
        )}
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Loan</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete loan {selectedLoan?.loanNumber}? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={confirmDelete} 
            color="error"
            disabled={deleteLoanMutation.isPending}
          >
            {deleteLoanMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LoanList;